import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, Clock, CheckCircle2, ChevronDown, ChevronUp, Send, Loader2, Check, Calendar, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import DOMPurify from 'dompurify';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicProfile() {
  const { slug } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [publishedContent, setPublishedContent] = useState([]);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axios.get(`${API}/profile/${slug}`);
        setProfile(res.data);
        // Fetch published content
        try {
          const contentRes = await axios.get(`${API}/profile/${slug}/content`);
          setPublishedContent(contentRes.data.content || []);
        } catch (e) { /* no published content */ }
      } catch (e) {
        setError('Profile not found');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [slug]);

  // SEO meta tags
  useEffect(() => {
    if (profile) {
      document.title = `${profile.business_name} — ${profile.city || ''} | GetChosenFirst`;
      const setMeta = (property, content) => {
        let tag = document.querySelector(`meta[property="${property}"]`) || document.querySelector(`meta[name="${property}"]`);
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute(property.startsWith('og:') ? 'property' : 'name', property);
          document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
      };
      setMeta('og:title', `${profile.business_name} — ${profile.tagline || ''}`);
      setMeta('og:description', profile.bio?.substring(0, 160) || `${profile.business_name} in ${profile.city}`);
      setMeta('og:type', 'profile');
      setMeta('og:url', window.location.href);
      setMeta('description', profile.bio?.substring(0, 160) || `${profile.business_name} in ${profile.city}`);
    }
    return () => { document.title = 'GetChosenFirst'; };
  }, [profile]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#1D6B4F] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex flex-col items-center justify-center px-6">
        <h2 className="font-display text-2xl text-[#3C3226] mb-3">Profile not found</h2>
        <p className="text-[#5C5347]">This business profile doesn't exist yet.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F4EF]" data-testid="public-profile-page">
      {/* JSON-LD Schema — sanitized to prevent XSS */}
      {profile.schema_markup && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(
            JSON.stringify(profile.schema_markup),
            { ALLOWED_TAGS: [], ALLOWED_ATTR: [] }
          ) }}
        />
      )}

      {/* Header */}
      <header className="text-[#F0EBE3] px-6 pt-10 pb-8" style={{ backgroundColor: '#2C2520' }}>
        <div className="max-w-lg mx-auto">
          <p className="v4-eyebrow mb-2" style={{ color: '#C67A5C' }}>GetChosenFirst Verified</p>
          <h1 className="font-display text-3xl font-medium mb-2" data-testid="profile-business-name">{profile.business_name}</h1>
          <p className="text-[#F0EBE3]/80 text-base mb-4">{profile.tagline}</p>
          <div className="flex items-center gap-4 text-sm text-[#F0EBE3]/70">
            <span className="flex items-center gap-1.5">
              <MapPin size={14} strokeWidth={1.5} /> {profile.city}{profile.state ? `, ${profile.state}` : ''}
            </span>
            {profile.years_in_business && (
              <span className="flex items-center gap-1.5">
                <Clock size={14} strokeWidth={1.5} /> {profile.years_in_business}+ years
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="px-6 py-6 max-w-lg mx-auto space-y-5">
        {/* About */}
        <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
          <h2 className="font-display text-xl font-medium text-[#3C3226] mb-3">About</h2>
          <p className="text-[#5C5347] leading-relaxed">{profile.bio}</p>
        </div>

        {/* Services */}
        {profile.services?.length > 0 && (
          <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
            <h2 className="font-display text-xl font-medium text-[#3C3226] mb-3">Services</h2>
            <div className="space-y-3">
              {profile.services.map((svc, i) => (
                <div key={svc.name || `svc-${i}`} className="flex items-start gap-3" data-testid={`service-${i}`}>
                  <CheckCircle2 size={18} strokeWidth={1.5} className="text-[#1D6B4F] mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-medium text-[#3C3226]">{svc.name}</h3>
                    <p className="text-sm text-[#5C5347]">{svc.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FAQ */}
        {profile.faqs?.length > 0 && (
          <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
            <h2 className="font-display text-xl font-medium text-[#3C3226] mb-3">Frequently Asked Questions</h2>
            <div className="space-y-2">
              {profile.faqs.map((faq, i) => (
                <div key={faq.question?.slice(0, 30) || `faq-${i}`} className="border border-[#D4C9B8] rounded-sm overflow-hidden" data-testid={`faq-${i}`}>
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === i ? null : i)}
                    className="w-full p-3 flex items-center justify-between text-left"
                  >
                    <span className="text-sm font-medium text-[#3C3226] pr-2">{faq.question}</span>
                    {expandedFaq === i ? <ChevronUp size={16} className="text-[#5C5347] flex-shrink-0" /> : <ChevronDown size={16} className="text-[#5C5347] flex-shrink-0" />}
                  </button>
                  {expandedFaq === i && (
                    <div className="px-3 pb-3 border-t border-[#D4C9B8]">
                      <p className="text-sm text-[#5C5347] mt-2 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Lead Capture Form */}
        {profile.lead_capture_enabled && (
          <LeadCaptureForm slug={slug} businessName={profile.business_name} />
        )}

        {/* Booking Widget (Calendly / Cal.com embed) */}
        {profile.booking_url && (
          <BookingWidget url={profile.booking_url} businessName={profile.business_name} />
        )}

        {/* Published Blog Content (auto-published from Content tab) */}
        {publishedContent.length > 0 && (
          <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]" data-testid="published-content">
            <h2 className="font-display text-xl font-medium text-[#3C3226] mb-4">Latest Articles</h2>
            <div className="space-y-4">
              {publishedContent.slice(0, 5).map(item => (
                <article key={item.content_id} className="border-b border-[#D4C9B8] pb-4 last:border-0 last:pb-0">
                  <h3 className="text-sm font-semibold text-[#3C3226] mb-1">{item.title}</h3>
                  <p className="text-xs text-[#5C5347] leading-relaxed line-clamp-3">{item.body}</p>
                  <p className="text-[10px] text-[#5C5347]/60 mt-2">
                    {new Date(item.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* Contact */}
        <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
          <h2 className="font-display text-xl font-medium text-[#3C3226] mb-3">Get in Touch</h2>
          <div className="space-y-3">
            {profile.phone && (
              <a href={`tel:${profile.phone}`} className="flex items-center gap-3 text-[#1D6B4F] hover:text-[#3C3226] transition-colors" data-testid="contact-phone">
                <Phone size={18} strokeWidth={1.5} />
                <span className="text-sm">{profile.phone}</span>
              </a>
            )}
            {profile.email && (
              <a href={`mailto:${profile.email}`} className="flex items-center gap-3 text-[#1D6B4F] hover:text-[#3C3226] transition-colors" data-testid="contact-email">
                <Mail size={18} strokeWidth={1.5} />
                <span className="text-sm">{profile.email}</span>
              </a>
            )}
            {profile.website && (
              <a href={profile.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-[#1D6B4F] hover:text-[#3C3226] transition-colors" data-testid="contact-website">
                <Globe size={18} strokeWidth={1.5} />
                <span className="text-sm">{profile.website}</span>
              </a>
            )}
            {!profile.phone && !profile.email && !profile.website && (
              <p className="text-sm text-[#5C5347]">Contact information will be added soon.</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-xs text-[#5C5347]">
            Powered by <span className="font-semibold text-[#1D6B4F]">GetChosenFirst</span>
          </p>
        </div>
      </main>
    </div>
  );
}

/* ── Lead Capture Form ── */
function LeadCaptureForm({ slug, businessName }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || (!email.trim() && !phone.trim())) return;
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/leads/${slug}`, {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        message: message.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="p-6 rounded-sm bg-[#1D6B4F] text-[#F0EBE3] text-center"
        data-testid="lead-form-success"
      >
        <div className="w-12 h-12 rounded-full bg-[#F0EBE3]/15 flex items-center justify-center mx-auto mb-3">
          <Check size={24} strokeWidth={2} />
        </div>
        <h3 className="font-display text-lg font-medium mb-1">Message sent!</h3>
        <p className="text-sm text-[#F0EBE3]/70">{businessName} will get back to you soon.</p>
      </motion.div>
    );
  }

  return (
    <div className="p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]" data-testid="lead-capture-form">
      <h2 className="font-display text-xl font-medium text-[#3C3226] mb-1">Get a Free Quote</h2>
      <p className="text-sm text-[#5C5347] mb-4">Send a message and {businessName} will respond shortly.</p>

      <form onSubmit={handleSubmit} className="space-y-3">
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Your name *"
          required
          className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent"
          data-testid="lead-name-input"
        />
        <div className="grid grid-cols-2 gap-3">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent"
            data-testid="lead-email-input"
          />
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder="Phone"
            className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent"
            data-testid="lead-phone-input"
          />
        </div>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="What do you need help with?"
          rows={3}
          className="w-full rounded-sm border border-[#D4C9B8] bg-[#F7F4EF] px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#3C3226] focus:border-transparent resize-none"
          data-testid="lead-message-input"
        />
        {error && <p className="text-xs text-[#E24B4A]">{error}</p>}
        <button
          type="submit"
          disabled={submitting || !name.trim() || (!email.trim() && !phone.trim())}
          className="w-full rounded-none bg-[#C67A5C] hover:bg-[#B3694C] disabled:opacity-40 text-[#F0EBE3] py-3.5 text-sm font-medium transition-all active:scale-[0.98] flex items-center justify-center gap-2"
          data-testid="lead-submit-btn"
        >
          {submitting ? <><Loader2 size={15} className="animate-spin" /> Sending...</> : <><Send size={15} /> Send Message</>}
        </button>
      </form>
    </div>
  );
}


function BookingWidget({ url, businessName }) {
  const [showEmbed, setShowEmbed] = useState(false);

  // Determine if Calendly or Cal.com for proper embed
  const isCalendly = url.includes('calendly.com');
  const isCal = url.includes('cal.com');

  // Normalize URL for embed
  const embedUrl = url.startsWith('http') ? url : `https://${url}`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-6 p-5 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]"
      data-testid="booking-widget"
    >
      <div className="flex items-center gap-2 mb-3">
        <Calendar size={18} strokeWidth={1.5} className="text-[#B8985A]" />
        <h3 className="text-base font-medium text-[#3C3226]">Book an Appointment</h3>
      </div>
      <p className="text-sm text-[#5C5347] mb-4">
        Schedule a consultation with {businessName || 'this professional'} directly.
      </p>

      {!showEmbed ? (
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowEmbed(true)}
            className="flex items-center gap-1.5 px-5 py-2.5 text-sm font-medium transition-all"
            style={{ backgroundColor: '#2C2520', color: '#F0EBE3' }}
            data-testid="show-booking-embed-btn"
          >
            <Calendar size={15} /> View Available Times
          </button>
          <a
            href={embedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs font-medium text-[#5C5347] hover:text-[#3C3226]"
            data-testid="booking-external-link"
          >
            <ExternalLink size={13} /> Open in new tab
          </a>
        </div>
      ) : (
        <div>
          <iframe
            src={embedUrl}
            title="Book an appointment"
            className="w-full border-0"
            style={{
              height: '630px',
              border: '1px solid #DDD5C8',
              backgroundColor: '#FDFBF7',
            }}
            data-testid="booking-iframe"
            loading="lazy"
          />
          <button
            onClick={() => setShowEmbed(false)}
            className="mt-2 text-xs text-[#5C5347] hover:text-[#3C3226]"
            data-testid="hide-booking-embed-btn"
          >
            Hide calendar
          </button>
        </div>
      )}
    </motion.div>
  );
}
